import {get, post} from './http';
import type {Submission} from './submissions';

export type P0AlertStatus = 'pending_approval' | 'approved' | 'rejected' | 'delivered';

export type P0Alert = {
  id: string;
  status: P0AlertStatus;
  subject: string | null;
  notes: string | null;
  // `notes` as plain text, extracted by the API - render this wherever notes show as text rather than
  // as the email body, so the SPA never parses stored HTML to get at it.
  notes_text: string | null;
  escalation_variants: string[];
  policy_violation: string;
  created_by: string;
  created_at: string;
  decided_by: string | null;
  decided_at: string | null;
  decision_reason: string | null;
  delivered_at: string | null;
  delivery_error: string | null;
  submissions: Submission[];
  /** Who delivery actually reached ("To: a", "Cc: b", "#channel"), recorded at send time. Reviewers only. */
  sent_recipients: string[];
  /** Reviewer-only preview of who an undelivered alert would notify right now, resolved live. */
  debug_recipients: string[];
};

export type P0AlertList = {
  results: P0Alert[];
  page: number;
  page_size: number;
  total_count: number;
};

export type P0AlertsQuery = {
  status?: P0AlertStatus;
  q?: string;
  policy_violation?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
};

export type CreateP0AlertRequest = {
  submission_ids: string[];
  subject?: string;
  notes?: string;
  escalation_variants?: string[];
  force_review?: boolean;
};

export type AmendP0AlertRequest = {
  subject?: string;
  notes?: string;
  escalation_variants?: string[];
  reason: string;
};

export const p0AlertsApi = {
  list: async (parameters?: P0AlertsQuery) => get<P0AlertList>('/p0-alerts', parameters),

  get: async (id: string) => get<P0Alert>(`/p0-alerts/${id}`),

  create: async (body: CreateP0AlertRequest) =>
    post<CreateP0AlertRequest, P0Alert>('/p0-alerts', body),

  approve: async (id: string) => post<undefined, P0Alert>(`/p0-alerts/${id}/approve`),

  reject: async (id: string, reason: string) =>
    post<{reason: string}, P0Alert>(`/p0-alerts/${id}/reject`, {reason}),

  amend: async (id: string, body: AmendP0AlertRequest) =>
    post<AmendP0AlertRequest, P0Alert>(`/p0-alerts/${id}/amend`, body),

  retryDelivery: async (id: string) => post<undefined, P0Alert>(`/p0-alerts/${id}/retry-delivery`)
};
